import { resolveHintAnchor, showAnchorHint } from '@/composables/useAnchorHint';

function prefersExecCommandCopy() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(pointer: coarse)')?.matches === true ||
    'ontouchstart' in window ||
    Number(navigator.maxTouchPoints) > 0
  );
}

/**
 * document.execCommand('copy') — 兼容 iOS / 微信等不支持 Clipboard API 的环境
 * @param {string} value
 */
function copyViaExecCommand(value) {
  if (typeof document === 'undefined') return false;

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', 'readonly');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.width = '2em';
  textarea.style.height = '2em';
  textarea.style.padding = '0';
  textarea.style.border = 'none';
  textarea.style.outline = 'none';
  textarea.style.boxShadow = 'none';
  textarea.style.background = 'transparent';
  textarea.style.opacity = '0';
  textarea.style.fontSize = '16px';

  document.body.appendChild(textarea);

  const selection = document.getSelection();
  /** @type {Range[]} */
  const savedRanges = [];
  if (selection) {
    for (let i = 0; i < selection.rangeCount; i += 1) {
      savedRanges.push(selection.getRangeAt(i));
    }
  }

  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, value.length);

  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }

  document.body.removeChild(textarea);

  if (selection) {
    selection.removeAllRanges();
    savedRanges.forEach((range) => selection.addRange(range));
  }

  return ok;
}

/**
 * @param {string} text
 * @returns {Promise<boolean>}
 */
export async function copyTextToClipboard(text) {
  const value = String(text ?? '').trim();
  if (!value) return false;

  if (prefersExecCommandCopy() && copyViaExecCommand(value)) return true;

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      /* fallback */
    }
  }

  return copyViaExecCommand(value);
}

/**
 * 复制文本并在触发元素旁展示纯文字 AnchorHint。
 * 锚点必须在 click 同步阶段捕获，避免 await 后 event.currentTarget 失效。
 * @param {object} options
 * @param {string} options.text
 * @param {HTMLElement | Event['currentTarget']} options.anchor
 * @param {string} options.successText
 * @param {string} [options.failText]
 */
export async function copyTextWithHint({ text, anchor, successText, failText }) {
  const anchorEl = resolveHintAnchor(anchor);
  const ok = await copyTextToClipboard(text);

  if (ok) {
    if (anchorEl && successText) {
      showAnchorHint({
        anchor: anchorEl,
        text: successText,
      });
    }
    return true;
  }

  if (failText && anchorEl) {
    showAnchorHint({
      anchor: anchorEl,
      text: failText,
    });
  }
  return false;
}

/**
 * H5 优先调原生分享；不可用时回退复制链接。
 * @param {object} options
 * @param {string} options.url
 * @param {string} [options.title]
 * @param {HTMLElement | Event['currentTarget']} options.anchor
 * @param {string} options.successText
 * @param {string} [options.failText]
 */
export async function shareOrCopyUrlWithHint({ url, title, anchor, successText, failText }) {
  const value = String(url ?? '').trim();
  if (!value) return false;

  const anchorEl = resolveHintAnchor(anchor);

  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      const shareTitle = String(title ?? '').trim();
      await navigator.share({
        url: value,
        ...(shareTitle ? { title: shareTitle } : {}),
      });
      if (anchorEl && successText) {
        showAnchorHint({ anchor: anchorEl, text: successText });
      }
      return true;
    } catch (err) {
      if (err?.name === 'AbortError') return false;
    }
  }

  return copyTextWithHint({
    text: value,
    anchor: anchorEl,
    successText,
    failText,
  });
}

/**
 * 在 click 处理器里同步调用，拿到稳定锚点。
 * @param {Event | HTMLElement} [source]
 */
export function hintAnchorFromEvent(source) {
  if (source instanceof HTMLElement) return resolveHintAnchor(source);
  if (source && typeof source === 'object' && 'currentTarget' in source) {
    const event = /** @type {Event} */ (source);
    return resolveHintAnchor(event.currentTarget ?? event.target ?? null);
  }
  return resolveHintAnchor(source);
}
